import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, ChevronRight, Trash2, RefreshCw,
  ListChecks, Shield, Users, UserCircle, Calendar,
} from "lucide-react";
import { formatMatchDate } from "@/lib/dates";

// -------- Helpers --------
const formatDate = (d) => formatMatchDate(d, { weekday: "short", day: "2-digit", month: "short", year: "numeric" }, "en-GB") || d;

// =============================================================
//  MATCH TAB  (preserves existing behaviour)
// =============================================================
const MatchesTab = () => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [selectedHash, setSelectedHash] = useState(null);
  const [groupDetail, setGroupDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [keeperId, setKeeperId] = useState(null);
  const [merging, setMerging] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/data-quality/duplicate-matches`, { withCredentials: true });
      setReport(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load duplicates report");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadReport(); }, [loadReport]);

  const openGroup = async (hash) => {
    setSelectedHash(hash); setGroupDetail(null); setKeeperId(null); setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/data-quality/duplicate-matches/${encodeURIComponent(hash)}`, { withCredentials: true });
      setGroupDetail(data);
      if (data.matches?.length) {
        const score = (m) => (m.event_count || 0) + (m.lineup_count || 0) + (m.shootout_kicks || 0) + (m.has_statistics ? 1 : 0);
        const best = [...data.matches].sort((a, b) => score(b) - score(a))[0];
        setKeeperId(best.match_id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load group details");
    } finally { setDetailLoading(false); }
  };

  const confirmMerge = async () => {
    if (!groupDetail || !keeperId) return;
    const dupIds = groupDetail.matches.filter(m => m.match_id !== keeperId).map(m => m.match_id);
    if (!dupIds.length) { toast.info("Nothing to merge"); return; }
    if (!window.confirm(`Merge ${dupIds.length} duplicate match(es) into the keeper? Events, lineups, stats and shootouts move to keeper. Duplicates are deleted. This cannot be undone.`)) return;
    setMerging(true);
    try {
      const { data } = await axios.post(`${API}/admin/data-quality/merge-matches`, { keeper_id: keeperId, duplicate_ids: dupIds }, { withCredentials: true });
      toast.success(`Merged ${data.deleted_match_ids.length} dups · moved ${data.moved.events} events, ${data.moved.lineups} lineups`);
      setGroupDetail(null); setSelectedHash(null); setKeeperId(null);
      loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally { setMerging(false); }
  };

  return (
    <div data-testid="dq-tab-matches">
      <Toolbar onRefresh={loadReport} loading={loading} />
      {loading ? <SkeletonList /> : (!report?.groups?.length ? <AllClear kind="matches" /> :
        <List
          items={report.groups}
          getKey={g => g.group_hash}
          onClick={g => openGroup(g.group_hash)}
          renderBadge={g => `×${g.count}`}
          renderTitle={g => formatDate(g.match_date)}
          renderSubtitle={g => g.match_ids.map(id => id.replace("match_", "")).join(" · ")}
          testIdPrefix="match-group-row"
        />)}

      <Dialog open={!!selectedHash} onOpenChange={(o) => { if (!o) { setSelectedHash(null); setGroupDetail(null); setKeeperId(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-bold uppercase">Resolve Duplicate Match Group</DialogTitle></DialogHeader>
          {detailLoading && <Skeleton className="h-64 w-full" />}
          {groupDetail && (
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <KV label="Competition" value={groupDetail.competition?.name} />
                <KV label="Date" value={formatDate(groupDetail.match_date)} />
                <KV label="Home" value={groupDetail.home_team?.name} />
                <KV label="Away" value={groupDetail.away_team?.name} />
              </div>
              <KeeperPicker items={groupDetail.matches} getId={m => m.match_id} keeperId={keeperId} setKeeperId={setKeeperId}
                render={m => (
                  <>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-xs text-gray-600">{m.match_id}</span>
                      <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 text-[10px] uppercase">{m.status || "—"}</Badge>
                      {m.source_match_id && <Badge variant="outline" className="text-[10px] font-mono">ext: {m.source_match_id}</Badge>}
                    </div>
                    <div className="mt-2 grid grid-cols-4 gap-2 text-xs">
                      <KV label="Score" value={`${m.home_score ?? "—"} : ${m.away_score ?? "—"}`} mono />
                      <KV label="Events" value={m.event_count} mono />
                      <KV label="Lineups" value={m.lineup_count} mono />
                      <KV label="Pens / Stats" value={`${m.shootout_kicks || 0} / ${m.has_statistics ? "✓" : "–"}`} mono />
                    </div>
                  </>
                )} />
              <MergeActions deletedCount={groupDetail.matches.length - 1} onCancel={() => setSelectedHash(null)} onConfirm={confirmMerge} merging={merging} keeperId={keeperId} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =============================================================
//  TEAMS TAB
// =============================================================
const TeamsTab = () => {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null); // array of team ids
  const [teamDetails, setTeamDetails] = useState(null); // array of teams w/ counts
  const [keeperId, setKeeperId] = useState(null);
  const [merging, setMerging] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/data-quality/duplicate-teams`, { withCredentials: true });
      setGroups(data.groups || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load teams report");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadReport(); }, [loadReport]);

  const openGroup = async (group) => {
    setSelectedGroup(group); setTeamDetails(null); setKeeperId(null);
    try {
      // For now the detail endpoint only accepts 2 at a time — show first 2
      // For groups >2, show all via the per-team quick lookup
      const details = [];
      for (const tid of group.team_ids) {
        const { data: ts } = await axios.get(`${API}/teams`);
        const t = ts.find(x => x.team_id === tid);
        if (!t) continue;
        details.push({
          ...t,
          player_count: null, // populated below
        });
      }
      // Grab richer counts via side-by-side endpoint when 2 teams
      if (group.team_ids.length === 2) {
        const { data } = await axios.get(`${API}/admin/data-quality/duplicate-teams/${group.team_ids[0]}/${group.team_ids[1]}`, { withCredentials: true });
        setTeamDetails(data.teams);
        const best = [...data.teams].sort((a, b) => (b.player_count + b.home_match_count + b.away_match_count) - (a.player_count + a.home_match_count + a.away_match_count))[0];
        setKeeperId(best?.team_id);
      } else {
        setTeamDetails(details);
        setKeeperId(details[0]?.team_id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load team details");
    }
  };

  const confirmMerge = async () => {
    if (!teamDetails || !keeperId) return;
    const dupIds = teamDetails.filter(t => t.team_id !== keeperId).map(t => t.team_id);
    if (!dupIds.length) return;
    if (!window.confirm(`Merge ${dupIds.length} duplicate team(s) into the keeper?\n\nPlayers, matches, standings and lineups will move to the keeper. The duplicate team records will be DELETED. This cannot be undone.`)) return;
    setMerging(true);
    try {
      const { data } = await axios.post(`${API}/admin/data-quality/merge-teams`, { keeper_id: keeperId, duplicate_ids: dupIds }, { withCredentials: true });
      toast.success(`Merged ${data.deleted_team_ids.length} teams · moved ${data.moved.players} players, ${data.moved.home_matches + data.moved.away_matches} matches`);
      setSelectedGroup(null); setTeamDetails(null); setKeeperId(null);
      loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally { setMerging(false); }
  };

  return (
    <div data-testid="dq-tab-teams">
      <Toolbar onRefresh={loadReport} loading={loading} />
      {loading ? <SkeletonList /> : (!groups.length ? <AllClear kind="teams" /> :
        <List
          items={groups}
          getKey={(g, i) => `tgroup-${i}`}
          onClick={g => openGroup(g)}
          renderBadge={g => `×${g.count}`}
          renderTitle={g => g.names.join(" · ")}
          renderSubtitle={g => g.reasons.join(" + ")}
          testIdPrefix="team-group-row"
        />)}

      <Dialog open={!!selectedGroup} onOpenChange={(o) => { if (!o) { setSelectedGroup(null); setTeamDetails(null); setKeeperId(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-bold uppercase">Resolve Duplicate Team Group</DialogTitle></DialogHeader>
          {!teamDetails && <Skeleton className="h-64 w-full" />}
          {teamDetails && (
            <div className="space-y-4">
              <WarningBox>
                Picking a KEEPER will merge <strong>all</strong> players, matches, standings and lineups from the other teams into it. The duplicate team records will be <strong>deleted</strong>.
              </WarningBox>
              <KeeperPicker items={teamDetails} getId={t => t.team_id} keeperId={keeperId} setKeeperId={setKeeperId}
                render={t => (
                  <>
                    <div className="flex items-center gap-3 flex-wrap">
                      {t.logo_url && <img src={t.logo_url} alt="" className="w-8 h-8 object-cover" />}
                      <div>
                        <p className="font-bold text-sm">{t.name}</p>
                        <p className="text-xs text-gray-500 font-mono">{t.team_id} · {t.short_name}</p>
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-4 gap-2 text-xs">
                      <KV label="Players" value={t.player_count ?? "—"} mono />
                      <KV label="Home matches" value={t.home_match_count ?? "—"} mono />
                      <KV label="Away matches" value={t.away_match_count ?? "—"} mono />
                      <KV label="Standings" value={t.standings_count ?? "—"} mono />
                    </div>
                    {(t.founded || t.stadium) && (
                      <p className="text-xs text-gray-500 mt-1">
                        {t.founded && `Founded ${t.founded}`} {t.stadium && `· ${t.stadium}`}
                      </p>
                    )}
                  </>
                )} />
              <MergeActions deletedCount={teamDetails.length - 1} onCancel={() => setSelectedGroup(null)} onConfirm={confirmMerge} merging={merging} keeperId={keeperId} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =============================================================
//  PLAYERS TAB
// =============================================================
const PlayersTab = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // { high_confidence, possible }
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [playerDetails, setPlayerDetails] = useState(null);
  const [keeperId, setKeeperId] = useState(null);
  const [merging, setMerging] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/data-quality/duplicate-players`, { withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load players report");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadReport(); }, [loadReport]);

  const openGroup = async (group) => {
    setSelectedGroup(group); setPlayerDetails(null); setKeeperId(null);
    try {
      if (group.players.length === 2) {
        const [a, b] = group.players;
        const { data } = await axios.get(`${API}/admin/data-quality/duplicate-players/${a.player_id}/${b.player_id}`, { withCredentials: true });
        setPlayerDetails(data.players);
        const best = [...data.players].sort((x, y) => ((y.event_count || 0) + (y.lineup_count || 0)) - ((x.event_count || 0) + (x.lineup_count || 0)))[0];
        setKeeperId(best?.player_id);
      } else {
        // >2 players — show summary rows without extra counts
        setPlayerDetails(group.players.map(p => ({ ...p, event_count: null, lineup_count: null })));
        setKeeperId(group.players[0]?.player_id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load player details");
    }
  };

  const confirmMerge = async () => {
    if (!playerDetails || !keeperId) return;
    const dupIds = playerDetails.filter(p => p.player_id !== keeperId).map(p => p.player_id);
    if (!dupIds.length) return;
    if (!window.confirm(`Merge ${dupIds.length} duplicate player(s) into the keeper?\n\nAll match events, assists and lineup entries will move to the keeper. The duplicate player records will be DELETED. This cannot be undone.`)) return;
    setMerging(true);
    try {
      const { data } = await axios.post(`${API}/admin/data-quality/merge-players`, { keeper_id: keeperId, duplicate_ids: dupIds }, { withCredentials: true });
      toast.success(`Merged ${data.deleted_player_ids.length} player(s) · moved ${data.moved.events} events, ${data.moved.secondary_events} assists, ${data.moved.lineups_updated} lineups`);
      setSelectedGroup(null); setPlayerDetails(null); setKeeperId(null);
      loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally { setMerging(false); }
  };

  const allGroups = [
    ...(data?.high_confidence || []).map(g => ({ ...g, confidence: "high" })),
    ...(data?.possible || []).map(g => ({ ...g, confidence: "possible" })),
  ];

  return (
    <div data-testid="dq-tab-players">
      <Toolbar onRefresh={loadReport} loading={loading} />
      {loading ? <SkeletonList /> : (allGroups.length === 0 ? <AllClear kind="players" /> :
        <>
          <div className="flex gap-4 mb-3 text-xs">
            <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{data?.high_confidence_count || 0} high confidence</Badge>
            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{data?.possible_count || 0} possible</Badge>
          </div>
          <List
            items={allGroups}
            getKey={(g, i) => `pgroup-${i}`}
            onClick={g => openGroup(g)}
            renderBadge={g => `×${g.players.length}`}
            renderTitle={g => g.players.map(p => p.name).join(" · ")}
            renderSubtitle={g => g.reason}
            testIdPrefix="player-group-row"
            getBadgeClass={g => g.confidence === "high" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}
          />
        </>)}

      <Dialog open={!!selectedGroup} onOpenChange={(o) => { if (!o) { setSelectedGroup(null); setPlayerDetails(null); setKeeperId(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-bold uppercase">Resolve Duplicate Player Group</DialogTitle></DialogHeader>
          {!playerDetails && <Skeleton className="h-64 w-full" />}
          {playerDetails && (
            <div className="space-y-4">
              <WarningBox>
                All match events, assists and lineup entries from the other player records will move into the keeper. The duplicate player records will be <strong>deleted</strong>. Pick carefully — this cannot be undone.
              </WarningBox>
              <KeeperPicker items={playerDetails} getId={p => p.player_id} keeperId={keeperId} setKeeperId={setKeeperId}
                render={p => (
                  <>
                    <div className="flex items-center gap-3 flex-wrap">
                      <div>
                        <p className="font-bold text-sm">{p.name}</p>
                        <p className="text-xs text-gray-500 font-mono">{p.player_id}</p>
                      </div>
                      {p.registration_id && <Badge className="bg-[#009E60] text-white hover:bg-[#009E60] text-[10px] font-mono">{p.registration_id}</Badge>}
                      {p.position && <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 text-[10px]">{p.position}</Badge>}
                    </div>
                    <div className="mt-2 grid grid-cols-4 gap-2 text-xs">
                      <KV label="DOB" value={p.date_of_birth || "—"} mono />
                      <KV label="Events" value={p.event_count ?? "—"} mono />
                      <KV label="Lineups" value={p.lineup_count ?? "—"} mono />
                      <KV label="Nationality" value={p.nationality || "—"} />
                    </div>
                  </>
                )} />
              <MergeActions deletedCount={playerDetails.length - 1} onCancel={() => setSelectedGroup(null)} onConfirm={confirmMerge} merging={merging} keeperId={keeperId} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =============================================================
//  Shared presentational pieces
// =============================================================
const Toolbar = ({ onRefresh, loading }) => (
  <div className="flex justify-end mb-4">
    <Button variant="outline" onClick={onRefresh} disabled={loading} className="rounded-none">
      <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
    </Button>
  </div>
);
const SkeletonList = () => <div className="space-y-3">{[0, 1, 2].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>;
const AllClear = ({ kind }) => (
  <div className="bg-white border border-gray-200 p-16 text-center" data-testid={`no-duplicates-${kind}`}>
    <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
    <h3 className="font-heading text-2xl font-black uppercase text-gray-700">All Clear</h3>
    <p className="text-gray-500 mt-2">No duplicate {kind} found. Keep going — prevention is working.</p>
  </div>
);
const List = ({ items, getKey, onClick, renderBadge, renderTitle, renderSubtitle, testIdPrefix, getBadgeClass }) => (
  <div className="bg-white border border-gray-200 divide-y divide-gray-200">
    {items.map((g, i) => (
      <button key={getKey(g, i)} onClick={() => onClick(g)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 text-left"
        data-testid={`${testIdPrefix}-${i}`}>
        <div className="flex items-center gap-4 min-w-0">
          <Badge className={`font-mono shrink-0 ${getBadgeClass?.(g) || "bg-orange-100 text-orange-700 hover:bg-orange-100"}`}>{renderBadge(g)}</Badge>
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">{renderTitle(g)}</p>
            <p className="text-xs text-gray-500 truncate">{renderSubtitle(g)}</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
      </button>
    ))}
  </div>
);
const KV = ({ label, value, mono }) => (
  <div>
    <p className="text-[10px] uppercase text-gray-500">{label}</p>
    <p className={`font-semibold ${mono ? "font-mono" : ""}`}>{value ?? "—"}</p>
  </div>
);
const WarningBox = ({ children }) => (
  <div className="bg-amber-50 border-l-4 border-amber-400 p-3 text-xs text-amber-800">{children}</div>
);
const KeeperPicker = ({ items, getId, keeperId, setKeeperId, render }) => (
  <div className="space-y-2">
    {items.map((it) => {
      const isKeeper = keeperId === getId(it);
      return (
        <label key={getId(it)}
          className={`flex items-start gap-4 p-4 border cursor-pointer ${isKeeper ? "border-[#009E60] bg-[#009E60]/5" : "border-gray-200 bg-white hover:border-gray-400"}`}
          data-testid={`candidate-${getId(it)}`}>
          <input type="radio" name={`keeper`} checked={isKeeper} onChange={() => setKeeperId(getId(it))}
            className="mt-1 h-4 w-4 accent-[#009E60]" data-testid={`radio-keeper-${getId(it)}`} />
          <div className="flex-1 min-w-0">
            {isKeeper && <Badge className="bg-[#009E60] text-white hover:bg-[#009E60] text-[10px] mb-1">KEEPER</Badge>}
            {render(it)}
          </div>
        </label>
      );
    })}
  </div>
);
const MergeActions = ({ deletedCount, onCancel, onConfirm, merging, keeperId }) => (
  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
    <p className="text-xs text-gray-500">{deletedCount} record(s) will be deleted</p>
    <div className="flex gap-2">
      <Button variant="outline" onClick={onCancel} className="rounded-none" data-testid="btn-cancel-merge">Cancel</Button>
      <Button onClick={onConfirm} disabled={merging || !keeperId}
        className="bg-red-500 hover:bg-red-600 text-white rounded-none" data-testid="btn-confirm-merge">
        <Trash2 className="w-4 h-4 mr-2" />
        {merging ? "Merging…" : `Merge & Delete ${deletedCount}`}
      </Button>
    </div>
  </div>
);

// =============================================================
//  ORPHAN CLEANUP (above the tabs)
// =============================================================
const OrphanCleanupPanel = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const dryRun = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/data-quality/clean-orphans?dry_run=true`, {}, { withCredentials: true });
      setReport(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  const commit = async () => {
    if (!window.confirm("Delete all orphan match events, lineups, statistics and penalty kicks? Standings will be recomputed from current matches. Non-destructive to real match data.")) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/data-quality/clean-orphans`, {}, { withCredentials: true });
      setReport(data);
      toast.success(`Cleaned ${data.deleted_events} events, ${data.deleted_lineups} lineups, ${data.deleted_statistics} stats, ${data.deleted_penalty_kicks} pens · standings recomputed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally { setLoading(false); }
  };

  const hasOrphans = report && (report.orphan_events + report.orphan_lineups + report.orphan_statistics + report.orphan_penalty_kicks) > 0;

  return (
    <div className="bg-white border border-gray-200 p-5 mb-6" data-testid="orphan-cleanup-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading font-bold uppercase text-sm text-gray-900">Orphan Record Cleanup</h3>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">
            Removes stray match events, lineups, statistics and penalty kicks that are no longer attached to any match
            (typically left over from testing or deleted matches). Then recomputes all standings from the current matches.
            Preserves every real match, team and player.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={dryRun} disabled={loading} className="rounded-none" data-testid="btn-orphan-dryrun">
            {loading && !report ? "…" : "Preview"}
          </Button>
          <Button onClick={commit} disabled={loading || !hasOrphans} className="bg-red-500 hover:bg-red-600 text-white rounded-none" data-testid="btn-orphan-commit">
            Clean orphans
          </Button>
        </div>
      </div>
      {report && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs" data-testid="orphan-report">
          {["orphan_events", "orphan_lineups", "orphan_statistics", "orphan_penalty_kicks", "orphan_goals"].map((k) => (
            <div key={k} className={`border p-2 ${report[k] > 0 ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200"}`}>
              <p className="text-[10px] uppercase text-gray-500">{k.replace("orphan_", "").replace("_", " ")}</p>
              <p className={`font-mono font-bold text-lg ${report[k] > 0 ? "text-amber-800" : "text-gray-500"}`}>
                {report[`deleted_${k.split("_")[1]}` ] ?? report[k]}
              </p>
            </div>
          ))}
          {report.dry_run === false && hasOrphans === false && (
            <div className="col-span-full text-green-700 text-xs mt-1">
              ✓ Database is clean. {report.standings_recomputed_competitions} competition(s) recomputed.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================
//  PAGE
// =============================================================
const AdminDataQuality = () => (
  <AdminLayout title="Data Quality">
    <div data-testid="admin-data-quality">
      <p className="text-gray-500 mb-6">Audit the database for duplicate matches, teams and players — then merge safely.</p>

      <div className="bg-green-50 border border-green-200 p-4 mb-6 flex items-start gap-3">
        <Shield className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
        <div className="text-sm">
          <p className="font-semibold text-green-900">Safeguards active</p>
          <p className="text-green-800">
            CSV imports are idempotent via <code className="font-mono">source_match_id</code>. New players and teams return a 409 if a near-duplicate exists — forcing admin confirmation. Every merge is written to the audit log.
          </p>
        </div>
      </div>

      <OrphanCleanupPanel />

      <Tabs defaultValue="matches">
        <TabsList className="rounded-none bg-white border border-gray-200 p-0 h-auto">
          <TabsTrigger value="matches" className="rounded-none data-[state=active]:bg-[#009E60] data-[state=active]:text-white px-5 py-3" data-testid="tab-matches">
            <Calendar className="w-4 h-4 mr-2" /> Matches
          </TabsTrigger>
          <TabsTrigger value="teams" className="rounded-none data-[state=active]:bg-[#009E60] data-[state=active]:text-white px-5 py-3" data-testid="tab-teams">
            <Users className="w-4 h-4 mr-2" /> Teams
          </TabsTrigger>
          <TabsTrigger value="players" className="rounded-none data-[state=active]:bg-[#009E60] data-[state=active]:text-white px-5 py-3" data-testid="tab-players">
            <UserCircle className="w-4 h-4 mr-2" /> Players
          </TabsTrigger>
        </TabsList>

        <TabsContent value="matches" className="mt-6"><MatchesTab /></TabsContent>
        <TabsContent value="teams" className="mt-6"><TeamsTab /></TabsContent>
        <TabsContent value="players" className="mt-6"><PlayersTab /></TabsContent>
      </Tabs>
    </div>
  </AdminLayout>
);

export default AdminDataQuality;
